import { getAuthHeaders } from './auth-headers';
import { throwResponseError } from './lock-error';

const API_URL = process.env.REACT_APP_CONVERSATIONS_API_URL;

// getAuthHeaders() is async (it acquires a fresh B2C token via MSAL), so every
// caller awaits it before issuing the request.
function headers() {
    return getAuthHeaders();
}

export async function listArtifacts(limit = 20, nextToken) {
    const params = new URLSearchParams({ limit: String(limit) });
    if (nextToken) params.set('nextToken', nextToken);

    const res = await fetch(`${API_URL}/artifacts?${params}`, {
        headers: await headers(),
    });

    if (!res.ok) throw new Error(`Failed to list artifacts: ${res.status}`);
    return res.json();
}

export async function createArtifact(data) {
    const res = await fetch(`${API_URL}/artifacts`, {
        method: 'POST',
        headers: await headers(),
        body: JSON.stringify(data),
    });

    if (!res.ok) throw new Error(`Failed to create artifact: ${res.status}`);
    return res.json();
}

export async function getArtifact(artifactId, version) {
    const params = version ? `?version=${version}` : '';
    const res = await fetch(`${API_URL}/artifacts/${artifactId}${params}`, {
        headers: await headers(),
    });

    if (!res.ok) {
        if (res.status === 404) throw new Error('Artifact not found');
        throw new Error(`Failed to get artifact: ${res.status}`);
    }
    return res.json();
}

export async function updateArtifact(artifactId, data) {
    const res = await fetch(`${API_URL}/artifacts/${artifactId}`, {
        method: 'PUT',
        headers: await headers(),
        body: JSON.stringify(data),
    });

    // Editing a locked PRD's content answers 409 { error, code, reason }.
    if (!res.ok) await throwResponseError(res, `Failed to update artifact: ${res.status}`);
    return res.json();
}

export async function getArtifactVersions(artifactId) {
    const res = await fetch(`${API_URL}/artifacts/${artifactId}/versions`, {
        headers: await headers(),
    });

    if (!res.ok) throw new Error(`Failed to get artifact versions: ${res.status}`);
    return res.json();
}

// Reads the backend's human-facing { error } message on a non-OK response.
// The approval endpoints answer 400 (manager unresolved) and 409 (invalid
// state) with a Spanish message meant to be shown to the author verbatim.
async function readErrorMessage(res, fallback) {
    try {
        const data = await res.json();
        if (data && data.error) return data.error;
    } catch (_) {
        /* body was not JSON */
    }
    return fallback;
}

// Submit a PRD for manager approval. Valid from `draft` (first submission) or
// `changes_requested` (re-submission). No request body — the backend resolves
// the manager and emails the magic links. Returns
// { status, submittedVersion, managerEmail, notified, resubmitted? }.
// `notified === false` means the transition succeeded but the email failed to
// send (the author can retry via resendNotification).
export async function submitForApproval(artifactId) {
    const res = await fetch(`${API_URL}/artifacts/${artifactId}/submit-for-approval`, {
        method: 'POST',
        headers: await headers(),
    });

    // Re-submitting from a non-submittable (locked) state answers 409
    // { error, code, reason }. throwResponseError raises a LockError on a
    // coded lock and a plain Error (reading `error`) otherwise, preserving the
    // 400 "manager unresolved" message shown verbatim to the author.
    if (!res.ok) await throwResponseError(res, `Failed to submit for approval: ${res.status}`);
    return res.json();
}

// Resend the approval email when a previous delivery failed. Valid only while
// the PRD is `pending_approval`. Returns { artifactId, status, notified }.
export async function resendNotification(artifactId) {
    const res = await fetch(`${API_URL}/artifacts/${artifactId}/resend-notification`, {
        method: 'POST',
        headers: await headers(),
    });

    if (!res.ok) {
        throw new Error(await readErrorMessage(res, `Failed to resend notification: ${res.status}`));
    }
    return res.json();
}
